import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth, useRole } from '../context/AuthContext';
import { authApi } from '../api/auth';
import { companyApi } from '../api/company';
import { useCloudSync } from '../hooks/useSync';
import { SyncStatus } from '../components/SyncStatus';
import { SubscriptionBanner } from '../components/SubscriptionBanner';

const IS_DESKTOP = !!import.meta.env.VITE_API_URL;

// NOT the same as IS_DESKTOP above — VITE_API_URL is also set for production
// *web* (cross-origin call to Cloud Run), so IS_DESKTOP is true there too.
// VITE_USE_HASH_ROUTER is the one flag that's genuinely desktop-only (see
// apps/desktop/.env.production) — use this for anything that must stay
// web-only, like the email-verification banner below (desktop has no email).
const IS_DESKTOP_APP = import.meta.env.VITE_USE_HASH_ROUTER === 'true';

const BASE_NAV = [
  { to: '/dashboard',      icon: '📊', label: 'Dashboard' },
  { to: '/invoices',       icon: '🧾', label: 'Invoices' },
  { to: '/parties',        icon: '🏢', label: 'Parties' },
  { to: '/products',       icon: '📦', label: 'Products' },
  { to: '/purchases',      icon: '🛒', label: 'Purchases' },
  { to: '/expenses',       icon: '💸', label: 'Expenses' },
  { to: '/payments',       icon: '💳', label: 'Payments' },
  { to: '/banking',        icon: '🏦', label: 'Banking' },
  { to: '/loans-capital',  icon: '🏛️', label: 'Loans & Capital' },
  { to: '/reports',        icon: '📋', label: 'Reports' },
  { to: '/manufacturing',  icon: '🏭', label: 'Manufacturing' },
  { to: '/templates',      icon: '🔁', label: 'Templates' },
  { to: '/users',          icon: '👥', label: 'Users' },
  { to: '/settings',       icon: '⚙️', label: 'Settings' },
  { to: '/feedback',       icon: '💬', label: 'Feedback' },
  { to: '/subscription',   icon: '⭐', label: 'Subscription' },
];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

function Avatar({ name }: { name: string }) {
  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div style={{
      width: 32, height: 32, borderRadius: '50%',
      background: 'linear-gradient(135deg,#3b82f6,#6366f1)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '0.72rem', fontWeight: 800, color: 'white', flexShrink: 0,
    }}>{initials}</div>
  );
}

export default function AppShell({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const { canManageUsers } = useRole();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { isConnected, isSyncing, pendingCount, lastSyncAt, conflictCount, sync } = useCloudSync();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent'>('idle');

  // Soft/advisory only — no email delivery exists on desktop, so don't nag there.
  const emailUnverified = !IS_DESKTOP_APP && !!user && !user.emailVerifiedAt;

  async function handleResendVerification() {
    setResendState('sending');
    try {
      await authApi.resendVerification();
      setResendState('sent');
    } catch {
      setResendState('idle');
    }
  }

  const { data: companyProfile, isLoading: profileLoading } = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => companyApi.get(),
    staleTime: 5 * 60 * 1000,
  });

  const isTransport = companyProfile?.natureOfBusiness === 'transport';

  // Show banner after load: profile missing entirely OR missing key contact/address fields.
  const profileIncomplete = !profileLoading && (
    !companyProfile ||
    !companyProfile.phone ||
    !companyProfile.addressLine1 ||
    !companyProfile.city ||
    !companyProfile.state
  );
  const rawNav = isTransport
    ? [
        BASE_NAV[0], // Dashboard
        BASE_NAV[1], // Invoices
        { to: '/lorry-receipts', icon: '🚚', label: 'Lorry Receipts' },
        ...BASE_NAV.slice(2),
      ]
    : BASE_NAV;
  const NAV = rawNav.filter(item => item.to !== '/users' || canManageUsers);
  const [theme, setTheme] = useState<'light' | 'dark'>(() =>
    (localStorage.getItem('cl_theme') as 'light' | 'dark') ?? 'light',
  );

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('cl_theme', theme);
  }, [theme]);

  function handleLogout() {
    logout();
    navigate('/login');
  }

  const sidebarW = collapsed ? 64 : 224;

  const sidebarStyle: React.CSSProperties = isMobile
    ? {
        position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 200,
        width: 224, background: 'var(--sidebar-bg)', display: 'flex',
        flexDirection: 'column', overflow: 'hidden',
        transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.22s cubic-bezier(0.4,0,0.2,1)',
        boxShadow: mobileOpen ? '8px 0 32px rgba(0,0,0,0.35)' : 'none',
      }
    : {
        width: sidebarW, minWidth: sidebarW, background: 'var(--sidebar-bg)',
        display: 'flex', flexDirection: 'column',
        transition: 'width 0.2s cubic-bezier(0.4,0,0.2,1)',
        overflow: 'hidden', flexShrink: 0,
        borderRight: '1px solid rgba(255,255,255,0.06)',
      };

  const displayName = user?.name ?? user?.email ?? '';

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Mobile backdrop */}
      {isMobile && mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            zIndex: 199, backdropFilter: 'blur(2px)',
          }}
        />
      )}

      {/* Sidebar */}
      <aside style={sidebarStyle}>
        {/* Brand */}
        <div style={{
          padding: '1.1rem 1rem',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          display: 'flex', alignItems: 'center', gap: '0.65rem',
          background: 'rgba(255,255,255,0.03)',
        }}>
          <div style={{
            width: 34, height: 34, borderRadius: 9,
            background: 'linear-gradient(135deg,#3b82f6,#6366f1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.1rem', flexShrink: 0, boxShadow: '0 2px 8px rgba(99,102,241,0.4)',
          }}>📊</div>
          {(isMobile || !collapsed) && (
            <div>
              <div style={{ color: 'white', fontWeight: 800, fontSize: '0.9rem', lineHeight: 1.15, letterSpacing: '-0.01em' }}>CoreLedger</div>
              <div style={{ color: 'var(--sidebar-text)', fontSize: '0.68rem', opacity: 0.7 }}>Billing Solution</div>
            </div>
          )}
          {isMobile && (
            <button
              onClick={() => setMobileOpen(false)}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--sidebar-text)', fontSize: '1.2rem', lineHeight: 1, padding: '0.2rem' }}
            >✕</button>
          )}
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '0.6rem 0.5rem', overflowY: 'auto' }}>
          {NAV.map(({ to, icon, label }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => isMobile && setMobileOpen(false)}
              style={({ isActive }) => ({
                display: 'flex', alignItems: 'center',
                gap: '0.7rem',
                padding: (!isMobile && collapsed) ? '0.7rem' : '0.6rem 0.85rem',
                justifyContent: (!isMobile && collapsed) ? 'center' : 'flex-start',
                color: isActive ? 'white' : 'var(--sidebar-text)',
                background: isActive ? 'rgba(59,130,246,0.18)' : 'transparent',
                borderRadius: '8px',
                margin: '1px 0',
                fontWeight: isActive ? 600 : 400,
                fontSize: '0.855rem',
                transition: 'background 0.15s, color 0.15s',
                textDecoration: 'none',
                borderLeft: isActive ? '3px solid #3b82f6' : '3px solid transparent',
              })}
              title={(!isMobile && collapsed) ? label : undefined}
            >
              <span style={{ fontSize: '1rem', flexShrink: 0 }}>{icon}</span>
              {(isMobile || !collapsed) && <span>{label}</span>}
            </NavLink>
          ))}
          {user?.isSuperAdmin && (
            <>
              <div style={{ height: '1px', background: 'rgba(255,255,255,0.07)', margin: '0.5rem 0.4rem' }} />
              <NavLink
                to="/admin/dashboard"
                onClick={() => isMobile && setMobileOpen(false)}
                style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center',
                  gap: '0.7rem',
                  padding: (!isMobile && collapsed) ? '0.7rem' : '0.6rem 0.85rem',
                  justifyContent: (!isMobile && collapsed) ? 'center' : 'flex-start',
                  color: isActive ? 'white' : '#f59e0b',
                  background: isActive ? 'rgba(245,158,11,0.15)' : 'transparent',
                  borderRadius: '8px',
                  margin: '1px 0',
                  fontWeight: isActive ? 600 : 400,
                  fontSize: '0.855rem',
                  transition: 'background 0.15s, color 0.15s',
                  textDecoration: 'none',
                  borderLeft: isActive ? '3px solid #f59e0b' : '3px solid transparent',
                })}
                title={(!isMobile && collapsed) ? 'Admin Panel' : undefined}
              >
                <span style={{ fontSize: '1rem', flexShrink: 0 }}>🛡️</span>
                {(isMobile || !collapsed) && <span>Admin Panel</span>}
              </NavLink>
            </>
          )}
        </nav>

        {/* Footer */}
        <div style={{
          padding: '0.75rem 0.75rem',
          borderTop: '1px solid rgba(255,255,255,0.07)',
          background: 'rgba(0,0,0,0.2)',
        }}>
          {(isMobile || !collapsed) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.6rem', padding: '0 0.1rem' }}>
              <Avatar name={displayName} />
              <div style={{ minWidth: 0 }}>
                <div style={{ color: 'white', fontWeight: 600, fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
                <div style={{ color: 'var(--sidebar-text)', fontSize: '0.7rem', textTransform: 'capitalize', opacity: 0.7 }}>{user?.role}</div>
              </div>
            </div>
          )}
          {collapsed && !isMobile && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.6rem' }}>
              <Avatar name={displayName} />
            </div>
          )}
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              padding: (!isMobile && collapsed) ? '0.5rem' : '0.45rem 0.75rem',
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '7px',
              color: 'var(--sidebar-text)', fontSize: '0.8rem',
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              justifyContent: (!isMobile && collapsed) ? 'center' : 'flex-start',
              transition: 'background 0.15s',
            }}
          >
            <span>🚪</span>{(isMobile || !collapsed) && 'Sign Out'}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        {/* Top bar */}
        <header style={{
          background: 'var(--surface)',
          borderBottom: '1px solid var(--border)',
          padding: '0 1rem',
          height: 54,
          display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0,
          boxShadow: '0 1px 0 var(--border)',
        }}>
          <button
            onClick={() => isMobile ? setMobileOpen(o => !o) : setCollapsed(c => !c)}
            style={{
              background: 'none', border: 'none', color: 'var(--text-2)',
              fontSize: '1.1rem', padding: '0.3rem 0.5rem', lineHeight: 1,
              borderRadius: '6px', transition: 'background 0.15s',
            }}
            title="Toggle sidebar"
          >☰</button>
          <div style={{ flex: 1 }} />
          {IS_DESKTOP && isConnected && (
            <SyncStatus
              isSyncing={isSyncing}
              pendingCount={pendingCount}
              lastSyncAt={lastSyncAt}
              conflictCount={conflictCount}
              onSync={sync}
            />
          )}
          <button
            onClick={() => setTheme(t => t === 'light' ? 'dark' : 'light')}
            style={{
              background: 'var(--bg)', border: '1.5px solid var(--border)',
              borderRadius: '8px', color: 'var(--text-2)',
              fontSize: '0.9rem', padding: '0.3rem 0.65rem', lineHeight: 1,
              transition: 'background 0.15s, border-color 0.15s',
            }}
            title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
          >
            {theme === 'light' ? '☾' : '☀'}
          </button>
        </header>

        {/* Incomplete profile banner */}
        {profileIncomplete && (
          <div style={{
            background: '#dc2626', color: 'white',
            padding: '0.55rem 1.25rem',
            display: 'flex', alignItems: 'center', gap: '0.6rem',
            fontSize: '0.85rem', fontWeight: 500, flexShrink: 0,
          }}>
            <span style={{ fontSize: '1rem' }}>⚠️</span>
            <span>Your company profile is incomplete. Add your phone, address, and state to start billing.</span>
            <NavLink
              to="/settings"
              style={{
                marginLeft: 'auto', background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: 6, padding: '0.25rem 0.75rem',
                color: 'white', fontWeight: 600, fontSize: '0.8rem',
                textDecoration: 'none', whiteSpace: 'nowrap',
              }}
            >
              Complete Profile →
            </NavLink>
          </div>
        )}

        {/* Advisory email-verification banner — never blocks the app */}
        {emailUnverified && (
          <div style={{
            background: '#d97706', color: 'white',
            padding: '0.55rem 1.25rem',
            display: 'flex', alignItems: 'center', gap: '0.6rem',
            fontSize: '0.85rem', fontWeight: 500, flexShrink: 0,
          }}>
            <span style={{ fontSize: '1rem' }}>✉️</span>
            <span>
              {resendState === 'sent'
                ? 'Verification email sent — check your inbox.'
                : 'Please verify your email address.'}
            </span>
            {resendState !== 'sent' && (
              <button
                onClick={handleResendVerification}
                disabled={resendState === 'sending'}
                style={{
                  marginLeft: 'auto', background: 'rgba(255,255,255,0.2)',
                  border: '1px solid rgba(255,255,255,0.4)',
                  borderRadius: 6, padding: '0.25rem 0.75rem',
                  color: 'white', fontWeight: 600, fontSize: '0.8rem',
                  cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                {resendState === 'sending' ? 'Sending…' : 'Resend verification email'}
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <main style={{ flex: 1, overflowY: 'auto', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
          <SubscriptionBanner />
          <div style={{ flex: 1 }}>{children}</div>
        </main>
      </div>
    </div>
  );
}
